import { Flex, useDisclosure } from "@chakra-ui/react";
import invariant from "invariant";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { LoadingIndicator } from "../../../components";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import ViewportPageContent from "../../../plan/layouts/ViewportPageContent";
import { clipDefaultEndTime } from "../../../utils/clip";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import {
  hideIntercomButton,
  showIntercomButton,
} from "../../../utils/intercom";
import {
  AiInterviewCallFragment,
  CallStatus,
  useAiInterviewCallQuery,
  useAiInterviewNotesQuery,
  useCallTranscriptLazyQuery,
  useViewCallMutation,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import { useIsExtension } from "../../hooks/useAppEnvironmentContext";
import useCurrentUser from "../../hooks/useCurrentUser";
import useMouseHasMoved from "../../hooks/useMouseHasMoved";
import { ClipRange, VideoDisplayMode } from "../../types";
import AiRecordingLayout from "../AiRecording/AiRecordingLayout";
import {
  AskPanel,
  introMessageForInterviews,
  suggestedInterviewMessages,
} from "../Candidate/Ask/AskPanel";
import CreateClipModal from "../CreateClipModal/CreateClipModal";
import CallErrorState from "../InterviewDetails/CallErrorState";
import useStartTime from "../InterviewDetails/useStartTime";
import useMediaPlayer from "../MediaPlayer/useMediaPlayer";
import useVideoState from "../MediaPlayer/useVideoState";
import { useAsk2Enabled } from "../Recording/SidebarTabs/Ai/Ask/useAsk2Enabled";
import useVisitorAnalytics from "../Recording/useVisitorAnalytics";
import AiInterviewHeader from "./AiInterviewHeader";
import AiInterviewMain from "./AiInterviewMain";
import { AiInterviewMediaPlayer } from "./AiInterviewMediaPlayer";
import AiInterviewSidebar from "./AiInterviewSidebar";
import { InterviewTab, isMainTab, isSidebarTab } from "./utils";

type AiInterviewDetailsProps = {
  callId: string;
  notesForCurrentUserOnly?: boolean;
};

export const AiInterviewDetails: React.FC<AiInterviewDetailsProps> = ({
  callId,
  notesForCurrentUserOnly = false,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const ask2Enabled = useAsk2Enabled();
  const signalsEnabled = useFeatureFlag(
    "ai_interviewer:interview_integrity:ui"
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const mainTabFromUrl = searchParams.get("main");
  const mainTabFromUrlValid =
    mainTabFromUrl &&
    isMainTab(mainTabFromUrl, isSmallScreen, signalsEnabled) &&
    (mainTabFromUrl !== InterviewTab.ASSIST || ask2Enabled);
  const defaultMainTab = mainTabFromUrlValid
    ? mainTabFromUrl
    : (searchParams.get("t") ?? searchParams.get("timestamp")) !== null
    ? InterviewTab.TRANSCRIPT
    : InterviewTab.EVALUATION;

  const sidebarTabFromUrl = searchParams.get("sidebar");
  const sidebarTabFromUrlValid =
    sidebarTabFromUrl &&
    isSidebarTab(sidebarTabFromUrl) &&
    (sidebarTabFromUrl !== InterviewTab.ASSIST || ask2Enabled);
  const defaultSidebarTab = sidebarTabFromUrlValid
    ? sidebarTabFromUrl
    : ask2Enabled
    ? InterviewTab.ASSIST
    : InterviewTab.NOTES;

  const [currentMainTab, setCurrentMainTab] = useState(defaultMainTab);
  const [currentSidebarTab, setCurrentSidebarTab] = useState(defaultSidebarTab);
  const currentTabs = [currentMainTab];
  if (!isSmallScreen) {
    currentTabs.push(currentSidebarTab);
  }

  const isExtension = useIsExtension();
  const askDisclosure = useDisclosure();
  const sendGAEvent = useSendGAEvent();
  const currentUser = useCurrentUser();
  const { recordingAllowed } = currentUser.organization;

  const { data, loading, error, stopPolling } = useAiInterviewCallQuery({
    variables: { id: callId },
    pollInterval: 5000,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  let call:
    | (AiInterviewCallFragment & {
        aiInterviewer: NonNullable<AiInterviewCallFragment["aiInterviewer"]>;
      })
    | null = null;

  if (data?.call) {
    const { aiInterviewer } = data.call;
    invariant(aiInterviewer, "Call must have an AI Interviewer");
    call = { ...data.call, aiInterviewer };
  }

  useEffect(() => {
    if (call?.status !== CallStatus.InProgress && !call?.isProcessing) {
      stopPolling();
    }
  }, [call?.status, call?.isProcessing, stopPolling]);

  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

  const [clipRange, setClipRange] = useState<ClipRange | null>(null);
  // Set up the media player & video
  const [mediaPlayerRef, listeners, player] = useMediaPlayer({
    enabled: !clipRange,
  });

  // Set up audio/video player controlls
  const { seek, time, play, canPlay, duration } = player;
  const seekAndPlay = useCallback(
    (t: number): void => {
      seek(t);
      if (!autoScrollEnabled) {
        setAutoScrollEnabled(true);
      }
      play();
    },
    [seek, autoScrollEnabled]
  );

  const onCreateClipClick = recordingAllowed
    ? (range?: ClipRange): void => {
        setClipRange({
          start: range?.start ?? time,
          end: range?.end ?? clipDefaultEndTime(call?.duration, time),
        });
      }
    : undefined;

  const startTime = useStartTime(duration);

  useEffect(() => {
    if (canPlay) {
      seek(startTime);
    }
  }, [canPlay, startTime, seek]);

  useEffect(() => {
    if (clipRange) {
      player.pause();
    }
  }, [clipRange, player]);

  useEffect(() => {
    searchParams.set("main", currentMainTab);
    setSearchParams(searchParams.toString(), { replace: true });
  }, [currentMainTab]);

  useEffect(() => {
    searchParams.set("sidebar", currentSidebarTab);
    setSearchParams(searchParams.toString(), { replace: true });
  }, [currentSidebarTab]);

  let videoDisplayMode = VideoDisplayMode.NO_RECORDING;
  if (call?.streamableAudio && !call?.streamableVideo) {
    videoDisplayMode = VideoDisplayMode.AUDIO;
  } else if (call?.streamableVideo) {
    videoDisplayMode = VideoDisplayMode.VIDEO;
  } else if (call?.isProcessing) {
    videoDisplayMode = VideoDisplayMode.PROCESSING;
  } else if (call?.status === CallStatus.InProgress) {
    videoDisplayMode = VideoDisplayMode.IN_PROGRESS;
  }

  const {
    containerRef,
    videoCallbackRef,
    fullScreenControls,
    pipControls,
    isVideoVisible,
    hideVideo,
    focusVideo,
  } = useVideoState(videoDisplayMode);

  const url = call?.streamableVideo
    ? call?.streamableVideo?.url
    : call?.streamableAudio?.url;
  const [mediaSrc, setMediaSrc] = useState(url);
  useEffect(() => {
    if (mediaSrc?.split("?")[0] !== url?.split("?")[0]) {
      setMediaSrc(url);
    }
  }, [url, mediaSrc]);

  // Log views to audit log & activity feed
  const [viewCall, { called: callViewLogged }] = useViewCallMutation();
  const mouseHasMoved = useMouseHasMoved();
  useEffect(() => {
    if (call && mouseHasMoved && !callViewLogged) {
      viewCall({ variables: { callId } });
    }
  }, [call, mouseHasMoved, callViewLogged]);

  // Log the view to segment
  useVisitorAnalytics({
    canLogCallView: callViewLogged,
    application: isExtension ? "extension" : "app",
    callId: call?.id,
    transcriptionStatus: call?.transcriptionStatus as string,
  });

  // Show/hide Intercom button based on tab
  const intercomEnabled = useFeatureFlag("interview-page-intercom");
  const currentIntercomTab = isSmallScreen ? currentMainTab : currentSidebarTab;

  useEffect(() => {
    if (
      intercomEnabled &&
      [
        InterviewTab.EVALUATION,
        InterviewTab.NOTES,
        ...(signalsEnabled ? [InterviewTab.SIGNALS] : []),
      ].includes(currentIntercomTab)
    ) {
      showIntercomButton();
    } else {
      hideIntercomButton();
    }
  }, [currentIntercomTab, intercomEnabled, signalsEnabled]);

  // restore intercom button on teardown
  useEffect(() => () => showIntercomButton(), []);

  const {
    data: notesData,
    loading: notesLoading,
    error: notesError,
  } = useAiInterviewNotesQuery({
    variables: {
      id: callId,
      notesForCurrentUserOnly,
    },
  });
  const notes = notesData?.call?.notes ?? [];

  const [
    callTranscriptQuery,
    {
      data: transcriptData,
      loading: transcriptLoading,
      error: transcriptError,
    },
  ] = useCallTranscriptLazyQuery({
    variables: { callId },
  });

  useEffect(() => {
    if (
      call &&
      call.status === CallStatus.Completed &&
      !call.isProcessing &&
      !transcriptData &&
      !transcriptLoading &&
      !transcriptError
    ) {
      callTranscriptQuery();
    }
  }, [
    call?.status,
    call?.isProcessing,
    transcriptData,
    transcriptLoading,
    transcriptError,
  ]);

  const segments = transcriptData?.call?.transcript ?? [];

  // If the call is loading or we're still figuring out if it was deleted
  // or never existed etc, show loading indicator
  if (loading) {
    return <LoadingIndicator pt="10vh" />;
  }

  if (error || !call) {
    // This can only happen if the query succeeded initially but an error occurs
    // when subsequently polling the same query.
    stopPolling();

    return (
      <Flex flexDirection="column" overflowY="hidden">
        <CallErrorState view="error" />
      </Flex>
    );
  }

  const actionButtons = undefined;

  // Extension mode: show AskPanel full screen when open
  if (isExtension && askDisclosure.isOpen) {
    return (
      <ViewportPageContent padding="0 0">
        <AskPanel
          requestPath={`/interview/${call.id}/ask2`}
          onClose={() => {
            sendGAEvent("ask_close", "call_review");
            askDisclosure.onClose();
          }}
          suggestedMessages={suggestedInterviewMessages}
          introMessage={introMessageForInterviews}
          eventCategory="call_review"
        />
      </ViewportPageContent>
    );
  }

  return (
    <AiRecordingLayout
      header={<AiInterviewHeader call={call} actionButtons={actionButtons} />}
      player={
        <AiInterviewMediaPlayer
          mediaPlayerRef={mediaPlayerRef}
          listeners={listeners}
          mediaSrc={mediaSrc}
          call={call}
          onSeek={seekAndPlay}
          onCreateClipClick={onCreateClipClick}
          player={player}
          containerRef={containerRef}
          notes={notes}
          videoDisplayMode={videoDisplayMode}
          segments={segments}
          isVideoVisible={isVideoVisible}
          videoRef={videoCallbackRef}
          hideVideo={hideVideo}
          showVideo={focusVideo}
          fullScreenControls={fullScreenControls}
          pipControls={pipControls}
        />
      }
      main={
        <AiInterviewMain
          currentTab={currentMainTab}
          onTabChange={setCurrentMainTab}
          call={call}
          notes={notes}
          notesLoading={notesLoading}
          notesError={notesError}
          transcript={segments}
          transcriptLoading={transcriptLoading}
          transcriptError={transcriptError}
          player={player}
          onSeek={seekAndPlay}
          onCreateClipClick={onCreateClipClick}
          autoScrollEnabled={autoScrollEnabled}
          setAutoScrollEnabled={setAutoScrollEnabled}
          notesForCurrentUserOnly={notesForCurrentUserOnly}
        />
      }
      sidebar={
        <AiInterviewSidebar
          currentTab={currentSidebarTab}
          onTabChange={setCurrentSidebarTab}
          call={call}
          notes={notes}
          notesLoading={notesLoading}
          notesError={notesError}
          player={player}
          onSeek={seekAndPlay}
          onCreateClipClick={onCreateClipClick}
          notesForCurrentUserOnly={notesForCurrentUserOnly}
          callId={call.id}
        />
      }
    >
      {clipRange && mediaSrc && (
        <CreateClipModal
          callId={call.id}
          mediaSrc={mediaSrc}
          initialClipRange={clipRange}
          onClipCreated={() => setCurrentSidebarTab(InterviewTab.CLIPS)}
          onClose={() => setClipRange(null)}
          positionId={call.position?.id}
          videoDisplayMode={videoDisplayMode}
        />
      )}
    </AiRecordingLayout>
  );
};
